import { useCallback, useEffect, useRef, useState } from 'react'

import { apiAbsoluteUrl } from '../app/runtime'
import { getAccessToken } from '../app/auth/tokenStore'

const TARGET_RATE = 16_000
const SILENCE_RMS = 0.012
// Auto-off: stop after sustained silence, and hard-stop regardless so an
// abandoned microphone cannot stream (and spend) indefinitely.
const SILENCE_STOP_MS = 4_000
const MAX_RECORDING_MS = 90_000
// After the user stops we ask the server to flush and wait briefly for
// the last committed transcript before closing the socket.
const FLUSH_GRACE_MS = 900

function downsampleToPcm16(input: Float32Array, inputRate: number): ArrayBuffer {
  const ratio = inputRate / TARGET_RATE
  const length = Math.floor(input.length / ratio)
  const output = new Int16Array(length)
  for (let i = 0; i < length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]))
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
  }
  return output.buffer
}

export function useVoiceInput(onTranscript: (committed: string, partial: string) => void) {
  const [recording, setRecording] = useState(false)
  const [level, setLevel] = useState(0)
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  const sessionRef = useRef<{
    socket: WebSocket
    context: AudioContext
    stream: MediaStream
    committed: string
    lastVoiceAt: number
    stopped: boolean
    hardCap: ReturnType<typeof setTimeout>
  } | null>(null)

  const stop = useCallback(() => {
    const session = sessionRef.current
    if (!session || session.stopped) return
    session.stopped = true
    clearTimeout(session.hardCap)
    for (const track of session.stream.getTracks()) track.stop()
    void session.context.close().catch(() => {})
    if (session.socket.readyState === WebSocket.OPEN) {
      session.socket.send(JSON.stringify({ type: 'flush' }))
      setTimeout(() => session.socket.close(), FLUSH_GRACE_MS)
    } else {
      session.socket.close()
    }
    setRecording(false)
    setLevel(0)
  }, [])

  const start = useCallback(async () => {
    if (sessionRef.current && !sessionRef.current.stopped) return
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch {
      return
    }

    const socket = new WebSocket(apiAbsoluteUrl('/api/voice/transcribe').replace(/^http/, 'ws'))
    const context = new AudioContext()
    const session = {
      socket,
      context,
      stream,
      committed: '',
      lastVoiceAt: Date.now(),
      stopped: false,
      hardCap: setTimeout(stop, MAX_RECORDING_MS),
    }
    sessionRef.current = session
    setRecording(true)

    let partial = ''
    socket.onopen = () => socket.send(JSON.stringify({ token: getAccessToken() ?? '' }))
    socket.onmessage = (event) => {
      let payload: { type?: string; text?: string }
      try {
        payload = JSON.parse(String(event.data))
      } catch {
        return
      }
      if (typeof payload.text !== 'string') return
      if (payload.type === 'partial') partial = payload.text
      else if (payload.type === 'final') {
        session.committed = `${session.committed}${session.committed ? ' ' : ''}${payload.text.trim()}`
        partial = ''
      } else return
      onTranscriptRef.current(session.committed, partial)
    }
    socket.onerror = stop
    socket.onclose = () => {
      if (!session.stopped) stop()
    }

    const source = context.createMediaStreamSource(stream)
    const processor = context.createScriptProcessor(4096, 1, 1)
    processor.onaudioprocess = (event) => {
      if (session.stopped) return
      const samples = event.inputBuffer.getChannelData(0)
      let sum = 0
      for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i]
      const rms = Math.sqrt(sum / samples.length)
      setLevel(Math.min(1, rms * 12))
      const now = Date.now()
      if (rms > SILENCE_RMS) session.lastVoiceAt = now
      else if (now - session.lastVoiceAt > SILENCE_STOP_MS) {
        stop()
        return
      }
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(downsampleToPcm16(samples, context.sampleRate))
      }
    }
    source.connect(processor)
    processor.connect(context.destination)
  }, [stop])

  useEffect(() => stop, [stop])

  return {
    supported:
      typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
    recording,
    level,
    start,
    stop,
  }
}
